import React, { useState, useEffect } from 'react';
import { getData, postData } from '../../../../services/apiService';
import { EventListView } from './EventListView';
import { EventDetailsView } from './EventDetailsView';
import { SettlementPopup } from './SettlementPopup';
import { ParticipantDetailsPopup } from './ParticipantDetailsPopup';

const PaymentOverview = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [view, setView] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParticipant, setSearchParticipant] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [showSettlementPopup, setShowSettlementPopup] = useState(false);
  const [settlementDates, setSettlementDates] = useState({
    fromDate: new Date().toISOString().split('T')[0],
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [processType, setProcessType] = useState('');
  const [showParticipantPopup, setShowParticipantPopup] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [participantDetails, setParticipantDetails] = useState(null);
  const [participantLoading, setParticipantLoading] = useState(false);
  const [participantError, setParticipantError] = useState(null);
  const [searchKey, setSearchKey] = useState(0);
  const [summary, setSummary] = useState({});

  useEffect(() => {
    fetchEventsPaymentData();
  }, [showSettlementPopup]);

  // In PaymentOverview.js, update the fetchEventsPaymentData function:

  const fetchEventsPaymentData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getData('Event/PaymentOverview');

      if (response.IsError == false) {
        // Set summary directly from API response
        setSummary(response?.Result?.Summary || {});

        const Data = response?.Result?.Data;
        const transformedEvents = Data.map(event => {
          const participants = event.Participants || [];
          const successParticipants = participants.filter(p => p.Status === "1");
          const cancelledParticipants = participants.filter(p => p.Status === "2");

          return {
            id: event.EventId,
            name: event.Event_Name,
            date: event.Start_datetime,
            venue: event.Event_Place,
            category: event.Event_type,
            totalParticipants: event.TotalParticipants || 0,
            totalAmount: parseFloat(event.TotalAmount) || 0,
            successAmount: successParticipants.reduce((sum, p) => sum + (parseFloat(p.Amount) || 0), 0),
            cancelledAmount: cancelledParticipants.reduce((sum, p) => sum + (parseFloat(p.Amount) || 0), 0),
            participants: participants.map(participant => ({
              id: participant.Id,
              name: participant.Name,
              email: participant.Email,
              amount: parseFloat(participant.Amount) || 0,
              statusCode: participant.Status,
              status: participant.Status === "1" ? "Paid Amount" : participant.Status === "2" ? "Not Paid" : "Pending",
              joinDate: participant.JoinDate,
              registrationId: participant.Reg_id
            })),
            successCount: successParticipants.length,
            cancelledCount: cancelledParticipants.length,
            eventId: event.EventId,
            institutionName: event.INST_NAME,
            startDatetime: event.Start_datetime,
            endDatetime: event.End_datetime,
            paymentStartDate: event.payment_startdate,
            paymentEndDate: event.payment_Enddate,
            organizerName: event.organizer_name,
            mobileNo: event.Mobile_No,
            organizerEmail: event.email,
            participantsCount: participants.length,
            paidParticipantsCount: successParticipants.length,
            notPaidParticipantsCount: cancelledParticipants.length,
          };
        });

        setEvents(transformedEvents);
      } else {
        throw new Error(response.Message || 'Failed to fetch payment data');
      }
    } catch (err) {
      setError(err.message || 'Failed to load payment data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchParticipantDetails = async (registrationId) => {
    if (!registrationId) return;

    try {
      setParticipantLoading(true);
      setParticipantError(null);
      setParticipantDetails(null);

      const response = await getData(`Event/Admib-Scrn-Reg-details?regId=${registrationId}`);

      if (response?.isError === false && response.data?.length > 0) {
        setParticipantDetails(response.data[0]);
      } else {
        throw new Error(response?.message || 'Failed to fetch participant details');
      }
    } catch (err) {
      setParticipantError(err.message || 'Failed to load participant details');
    } finally {
      setParticipantLoading(false);
    }
  };

  const handleParticipantClick = async (participant) => {
    setSelectedParticipant(participant);
    setShowParticipantPopup(true);
    await fetchParticipantDetails(participant.registrationId);
  };

  const handleSettlementProcess = () => {
    setProcessType('settlement');
    setShowSettlementPopup(true);
  };

  const handleReprocess = async () => {
    setProcessType('reprocess');
    const today = new Date().toISOString().split('T')[0];
    await executeProcess('reprocess', { fromDate: today, toDate: today });
  };

  const executeProcess = async (type, dates) => {
    setIsProcessing(true);
    setProcessingMessage(`${type === 'settlement' ? 'Processing settlement' : 'Reprocessing'}...`);

    try {
      let response;

      if (type === 'settlement') {
        response = await getData(`Report/paytm-settlement?date=${dates.fromDate}`);
      } else {
        response = await postData('Paytm/reprocess', dates);
      }

      if (response?.IsError === false || response.success == true) {
        setProcessingMessage(`${type === 'settlement' ? 'Settlement' : 'Reprocess'} completed successfully!`);
        await fetchEventsPaymentData();
      } else {
        throw new Error(response?.Message || `Failed to ${type}`);
      }
    } catch (err) {
      setProcessingMessage(`Error: ${err.message}`);
    } finally {
      setIsProcessing(false);
      setShowSettlementPopup(false);
    }
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setView('details');
    setCurrentPage(1);
    setFilterStatus('all');
    setSortField(null);
    setSortDirection('asc');
  };

  const handleBackToList = () => {
    setSelectedEvent(null);
    setView('list');
    setSearchKey(prev => prev + 1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const filteredEvents = events.filter(event =>
    event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.venue.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.category.toString().toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedEvents = [...filteredEvents].sort((a, b) => {
    switch (sortBy) {
      case 'date':
        return new Date(b.date) - new Date(a.date);
      case 'participants':
        return b.paidParticipantsCount - a.paidParticipantsCount;
      case 'amount':
        return b.successAmount - a.successAmount;
      case 'name':
      default:
        return a.name.localeCompare(b.name);
    }
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30">
      {view === 'list' ? (
        <EventListView
          summary={summary}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortedEvents={sortedEvents}
          events={events}
          loading={loading}
          error={error}
          handleEventClick={handleEventClick}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          onSettlementProcess={handleSettlementProcess}
          onReprocess={handleReprocess}
          isProcessing={isProcessing}
          searchKey={searchKey}
        />
      ) : (
        <EventDetailsView
          selectedEvent={selectedEvent}
          handleBackToList={handleBackToList}
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          searchParticipant={searchParticipant}
          setSearchParticipant={setSearchParticipant}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          sortField={sortField}
          sortDirection={sortDirection}
          handleSort={handleSort}
          handleParticipantClick={handleParticipantClick}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          onSettlementProcess={handleSettlementProcess}
          onReprocess={handleReprocess}
          isProcessing={isProcessing}
        />
      )}

      <SettlementPopup
        show={showSettlementPopup}
        onClose={() => setShowSettlementPopup(false)}
        onSubmit={executeProcess}
        isProcessing={isProcessing}
        processingMessage={processingMessage}
        settlementDates={settlementDates}
        setSettlementDates={setSettlementDates}
      />

      <ParticipantDetailsPopup
        show={showParticipantPopup}
        onClose={() => {
          setShowParticipantPopup(false);
          setParticipantDetails(null);
          setParticipantError(null);
        }}
        participant={selectedParticipant}
        details={participantDetails}
        loading={participantLoading}
        error={participantError}
        onRetry={() => fetchParticipantDetails(selectedParticipant?.registrationId)}
        formatCurrency={formatCurrency}
        formatDate={formatDate}
      />
    </div>
  );
};

export default PaymentOverview;